import React from 'react';
import { Controller, type Control, type FieldValues, type Path } from 'react-hook-form';
import { ChevronDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
  Field,
  FieldDescription,
  FieldError,
  FieldLabel,
} from '@/components/ui';
import type { AcademySport } from '@court-sync/shared';
import { useAcademySports } from '@/queries/academySports';
import { getAcademySportLabel } from '@/utils/sportPresentation';
import { cn } from '@/lib/utils';

type CourtSportOption = Pick<AcademySport, 'id' | 'sportId' | 'customLabel' | 'isActive' | 'isPrimary'>;

export interface AcademySportMultiSelectFieldProps<T extends FieldValues> {
  academyId?: string | null;
  /** When set, uses these sports instead of loading from the academy API (e.g. club facility courts). */
  sports?: CourtSportOption[];
  control: Control<T>;
  name: Path<T>;
  label?: string;
  description?: string;
  emptyStateMessage?: string;
  disabled?: boolean;
  className?: string;
}

export function AcademySportMultiSelectField<T extends FieldValues>({
  academyId,
  sports: sportsOverride,
  control,
  name,
  label = 'Sports',
  description = 'Select which sports this court supports.',
  emptyStateMessage = 'Configure academy sports in Academy Settings before assigning courts.',
  disabled = false,
  className,
}: AcademySportMultiSelectFieldProps<T>) {
  const { sports: fetchedSports, isLoading: fetchedLoading } = useAcademySports(academyId, {
    enabled: !!academyId && !sportsOverride,
  });
  const sports = sportsOverride ?? fetchedSports;
  const isLoading = sportsOverride ? false : fetchedLoading;
  const activeSports = sports.filter((s) => s.isActive);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const [menuWidth, setMenuWidth] = React.useState<number | undefined>();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => {
        const selected: string[] = Array.isArray(field.value) ? field.value : [];
        const selectedLabels = activeSports
          .filter((sport) => selected.includes(sport.id))
          .map((sport) => getAcademySportLabel(sport));
        const summary =
          selectedLabels.length === 0
            ? 'Select sports…'
            : selectedLabels.length <= 2
              ? selectedLabels.join(', ')
              : `${selectedLabels.length} sports selected`;

        const toggle = (sportId: string, checked: boolean) => {
          const next = checked
            ? selected.includes(sportId)
              ? selected
              : [...selected, sportId]
            : selected.filter((id) => id !== sportId);
          field.onChange(next);
        };

        const isDisabled = disabled || isLoading || activeSports.length === 0;

        return (
          <Field data-invalid={!!fieldState.error?.message} className={className}>
            <FieldLabel htmlFor={`${String(name)}-trigger`}>
              {label} *
            </FieldLabel>
            <DropdownMenu
              onOpenChange={(open) => {
                if (open && triggerRef.current) {
                  setMenuWidth(triggerRef.current.offsetWidth);
                }
              }}
            >
              <DropdownMenuTrigger asChild>
                <button
                  ref={triggerRef}
                  type="button"
                  id={`${String(name)}-trigger`}
                  disabled={isDisabled}
                  className={cn(
                    'input flex h-9 w-full items-center justify-between gap-2 text-left text-sm shadow-sm',
                    'focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500',
                    'disabled:cursor-not-allowed disabled:opacity-50',
                    selectedLabels.length === 0 && 'text-muted-foreground'
                  )}
                >
                  <span className="truncate">{summary}</span>
                  <ChevronDown className="h-4 w-4 shrink-0 opacity-50" aria-hidden />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                style={menuWidth ? { width: menuWidth } : undefined}
              >
                {activeSports.map((sport) => (
                  <DropdownMenuCheckboxItem
                    key={sport.id}
                    checked={selected.includes(sport.id)}
                    disabled={disabled}
                    onCheckedChange={(checked) => toggle(sport.id, checked === true)}
                    onSelect={(event) => event.preventDefault()}
                  >
                    {getAcademySportLabel(sport)}
                    {sport.isPrimary ? ' (primary)' : ''}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            {description ? (
              <FieldDescription className="text-xs">{description}</FieldDescription>
            ) : null}
            {isLoading ? (
              <p className="mt-1 text-xs text-muted-foreground">Loading sports…</p>
            ) : activeSports.length === 0 ? (
              <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">{emptyStateMessage}</p>
            ) : null}
            <FieldError>{fieldState.error?.message}</FieldError>
          </Field>
        );
      }}
    />
  );
}
